import { ComponentProps, useState } from "react";

import ViewQuoteRequestDialogView from "app/pages/request-management/views/quotes/ViewQuoteRequestDialogView";
import OverflowMenuView from "components/OverflowMenuView";
import withAsync from "components/withAsync";
import noop from "lodash/noop";
import CreateTemplateDialogView from "pages/shipper/components/CreateTemplateDialogView";
import useCreateTemplateDialog from "pages/shipper/hooks/useCreateTemplateDialog";
import { useTranslation } from "react-i18next";

import useLoadQuoteRequest from "../../hooks/quotes/useLoadQuoteRequest";
import CloseQuoteRequestDialog from "./CloseQuoteRequestDialog";
import DuplicateQuoteRequest from "./DuplicateQuoteRequest";
import ResendQuoteRequestDialog from "./ResendQuoteRequestDialog";

const QuoteRequestHeaderActions = withAsync({
  useHook: useLoadQuoteRequest,
  Component: ({ loadedData: { quoteRequest, quoteSubmissionLink } }) => {
    const { t } = useTranslation(["requestManagement"]);
    const createTemplateDialog = useCreateTemplateDialog({ quoteRequestId: quoteRequest.id });
    const [isViewQuoteRequestDialogOpen, setIsViewQuoteRequestDialogOpen] = useState(false);
    const [isDuplicatingQuoteRequest, setIsDuplicatingQuoteRequest] = useState(false);
    const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
    const [isCloseQuoteDialogOpen, setIsCloseQuoteDialogOpen] = useState(false);

    const actions: ComponentProps<typeof OverflowMenuView>["items"] = [
      {
        label: t("requestManagement:quoteRequestHeader_actions_viewQuoteRequest_label"),
        onClick: () => setIsViewQuoteRequestDialogOpen(true),
        disabled: quoteRequest.mode !== "FTL",
      },
      {
        label: t("requestManagement:quoteRequestHeader_actions_saveAsTemplate_label"),
        onClick: createTemplateDialog.dialogOpen.setTrue,
      },
      {
        label: t("requestManagement:quoteRequestHeader_actions_cancelShipment_label"),
        onClick: noop,
        disabled: true /** @todo */,
      },
      {
        label: t("requestManagement:quoteRequestHeader_actions_duplicateQuoteRequest_label"),
        onClick: () => setIsDuplicatingQuoteRequest(true),
        disabled: false,
      },
      {
        label: t("requestManagement:quoteRequestHeader_actions_resendQuoteRequest_label"),
        onClick: () => setIsResendDialogOpen(true),
        disabled: quoteRequest.status !== "SENT" && quoteRequest.status !== "RETURNED",
      },
      {
        label: t("requestManagement:quoteRequestHeader_actions_closeQuote_label"),
        onClick: () => setIsCloseQuoteDialogOpen(true),
        disabled: quoteRequest.status !== "SENT" && quoteRequest.status !== "RETURNED",
      },
    ];

    return (
      <>
        <OverflowMenuView label={t("requestManagement:quoteRequestHeader_actions_label")} items={actions} />
        <CreateTemplateDialogView
          isOpen={createTemplateDialog.dialogOpen.value}
          onClose={createTemplateDialog.dialogOpen.setFalse}
          onSubmit={createTemplateDialog.onSubmit}
          loading={createTemplateDialog.loading}
        />
        <ViewQuoteRequestDialogView
          quoteRequest={quoteRequest}
          submissionLink={quoteSubmissionLink || ""}
          isOpen={isViewQuoteRequestDialogOpen}
          onClose={() => setIsViewQuoteRequestDialogOpen(false)}
        />
        <DuplicateQuoteRequest.Drawer
          isOpen={isDuplicatingQuoteRequest}
          onClose={() => setIsDuplicatingQuoteRequest(false)}
        />
        <ResendQuoteRequestDialog
          quoteRequest={quoteRequest}
          open={isResendDialogOpen}
          onClose={() => setIsResendDialogOpen(false)}
        />
        <CloseQuoteRequestDialog
          quoteRequest={quoteRequest}
          open={isCloseQuoteDialogOpen}
          onClose={() => setIsCloseQuoteDialogOpen(false)}
        />
      </>
    );
  },
});

export default QuoteRequestHeaderActions;
